import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { signupDefaultValues, signupSchema } from './assets/authUtil';
import {
  useLoginMutation,
  useSignupMutation,
  setCredentials,
  setUserBasicInfo,
} from './authSlice';
import FormikInputField from '../../app/components/formikComponents/FormikInputField';
import StyledButton from '../../app/components/styledComponents/StyledButton';
import { toast } from 'react-toastify';
import { Col, Container, OverlayTrigger, Row } from 'react-bootstrap';
import BoostFloralAvatarIcon from '../../app/components/svgIcons/BoostFloralAvatarIcon';
import BoostFloralTextIcon from '../../app/components/svgIcons/BoostFloralTextIcon';
import StyledText from '../../app/components/styledComponents/StyledText';
import { setTheme } from '../../app/services/themeSlice';
import { getTheme } from '../../utils/helper';
import { colorPaletteOptions } from '../settings/assets/settingsUtils';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useVerifyCaptchaMutation } from '../landingPage/pages/publicPagesAPISlice';
import ReCAPTCHA from 'react-google-recaptcha';
import Modal from 'react-bootstrap/esm/Modal';
import styled from 'styled-components';
import { StyledTooltip } from '../../app/components/styledComponents/StyledTooltip';
import ClipboardText from '../../app/components/svgIcons/ClipboardText';
import StyledInputWithIcon from '../../app/components/styledComponents/StyledInputWithIcon';
import * as Yup from 'yup';
import SeoHead from '../../app/components/SeoHead';

const validationSchema = Yup.object().shape({
  passwordLength: Yup.number()
    .min(8, 'Password length must be at least 8')
    .max(30, 'Password length cannot be more than 30')
    .required('Password length is required'),
  passwordStrength: Yup.number()
    .oneOf(
      [1, 2, 3],
      'Password strength must be 1 (low), 2 (medium), or 3 (high)',
    )
    .required('Password strength is required'),
  newPassword: Yup.string().required('New password is required'),
});

function generatePassword(length, strength) {
  let charset;
  const specialCharset = '!@#$%^&*()_+~`|}{[]:;?><,./-=';

  switch (Number(strength)) {
    case 1:
      charset = 'abcdefghijklmnopqrstuvwxyz';
      break;
    case 2:
      charset =
        'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      break;
    case 3:
      charset =
        'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789' +
        specialCharset;
      break;
    default:
      throw new Error(
        'Invalid strength level. Please choose "low", "medium", or "high".',
      );
  }

  let password = '';
  for (let i = 0; i < length; i++) {
    let randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }

  // Ensure at least one special character in high strength password
  if (strength === 2) {
    let hasSpecialChar = specialCharset
      .split('')
      .some((char) => password.includes(char));
    if (!hasSpecialChar) {
      let specialIndex = Math.floor(Math.random() * specialCharset.length);
      let replaceIndex = Math.floor(Math.random() * length);
      password =
        password.substring(0, replaceIndex) +
        specialCharset[specialIndex] +
        password.substring(replaceIndex + 1);
    }
  }

  return password;
}

const Signup = () => {
  const dispatch = useDispatch();
  const ref = useRef();

  const [signup, { isLoading: loading }] = useSignupMutation();
  const [login, { isLoading: loadingData }] = useLoginMutation();
  const [verifyCaptcha, { isLoading: verifyingCaptcha }] =
    useVerifyCaptchaMutation();
  const [recaptchaVerified, setRecaptchaVerified] = useState(
    !process.env.REACT_APP_ENV !== 'dev',
  );
  const [showInvalidCaptchaMessage, setShowInvalidCaptchaMessage] =
    useState(false);

  const handleRecaptchaValidation = async (value) => {
    const res = await verifyCaptcha({ recaptchaToken: value });

    if (res?.data?.includes('Validated')) {
      setRecaptchaVerified(true);
    } else {
      setRecaptchaVerified(false);
      setShowInvalidCaptchaMessage(true);
      ref.current.reset();

      setTimeout(() => {
        setShowInvalidCaptchaMessage(false);

      }, 2000);
    }
  };

  const seoData = {
    title: 'Sign Up for BoostFloral | The Best Floral Website Platform for Flower Shops',
    description:
      'Join BoostFloral today and launch a beautiful, easy-to-manage florist website designed to boost orders and grow your flower shop online. Sign up now and experience the floral industry’s best website solution, trusted by florists for stunning designs, fast performance, and powerful features!',
    keywords:
      'Flower Shop Websites, Floral Web Design Software, Floral CMS, Building a Floral Website, Floral POS, Floral point of sale, Floral website builder, Build a Floral Website, Florist website design, Floral website design, best floral website templates, Florist web templates, Create a flower shop website, Floral e-commerce website, Best website platform for florists, Flower shop online store, Florist online ordering system, Flower delivery website, POS system for florists, Florist website SEO, Customizable florist website, Floral event booking website, the best website for retail flower shops',
    ogTitle: 'Sign Up for BoostFloral | The Best Floral Website Platform for Flower Shops',
    ogDescription:
    'Join BoostFloral today and launch a beautiful, easy-to-manage florist website designed to boost orders and grow your flower shop online. Sign up now and experience the floral industry’s best website solution, trusted by florists for stunning designs, fast performance, and powerful features!',
    ogType: 'company',
    ogSiteName: 'BoostFloral',
    ogImage: 'https://www.boostfloral.com/logo.svg',
  };

  return (
    <div className="px-3 px-md-5 pb-3 d-flex align-items-center flex-grow-1">
      <SeoHead data={seoData}/>
      <Container className="auth-inner bg-white rounded-3 mt-3 p-3 p-md-3 px-md-4 pb-md-1">
        <Row>
          <Col xs={12}>
            <h2 className="mb-2 mb-md-4">Create an Account</h2>
          </Col>
          <Col xs={12} md={5}>
            <div className=" flex-column gap-3 gap-md-4 flex-center">
              <BoostFloralAvatarIcon size={150} />
              <BoostFloralTextIcon />
            </div>

            <div className="flex-column gap-3 d-flex mt-4 mb-4 mb-md-0">
              <p className="mb-0 pe-3">
                Be a part of a Huge Florist Community that helps you learn and
                grow as a Business.
              </p>
              <div>
                <StyledText color="secondary" as="h6" className="fw-bold mb-1">
                  LEARN.
                </StyledText>
                <StyledText color="secondary" as="h6" className="fw-bold mb-1">
                  COLLABORATE.
                </StyledText>
                <StyledText color="secondary" as="h5" className="fw-bold mb-1">
                  BOOST YOUR BUSINESS.
                </StyledText>
              </div>
            </div>
          </Col>
          <Col xs={12} md={7}>
            <Formik
              initialValues={{
                ...signupDefaultValues,
              }}
              validationSchema={signupSchema}
              onSubmit={async (values) => {
                if (!recaptchaVerified) return;

                const respo = await signup({ ...values });

                if (
                  !respo.error &&
                  respo?.data?.message !== 'Email Address Already Exist.'
                ) {
                  const { data } = await login({ ...values });
                  if (data?.accessToken) {
                    dispatch(
                      setUserBasicInfo({
                        values:
                          {
                            firstName: data?.user?.firstName || '',
                            lastName: data?.user?.lastName || '',
                            imageUrl: data?.user?.imageUrl || '',
                          } || {},
                      }),
                    );
                    dispatch(setCredentials({ values: data }));
                    const themeSettings = data.user?.themeSettings;
                    const { customColors, colorPalette } = themeSettings
                      ? JSON.parse(themeSettings)
                      : {
                          customColors: colorPaletteOptions[0].primaryColors,
                          colorPalette: 'default',
                        };
                    const option =
                      colorPaletteOptions.find(
                        ({ value }) => value === colorPalette,
                      ) || colorPaletteOptions[0];

                    dispatch(
                      setTheme({ theme: getTheme(option, customColors) }),
                    );
                  }
                  toast.success(
                    respo?.respo?.message || 'User registered successfully',
                  );
                } else {
                  toast.error(respo?.data?.message);
                }

                setRecaptchaVerified(!process.env.REACT_APP_ENV !== 'dev');
                ref.current.reset();
              }}
              render={({ values }) => (
                <Form>
                  <div className="mb-2">
                    <StyledText
                      as="label"
                      color="secondary"
                      htmlFor={`email`}
                      className="form-label"
                    >
                      Email *
                    </StyledText>
                    <FormikInputField
                      name={`email`}
                      id={`email`}
                      placeholder="Type in"
                    />
                  </div>
                  <div className="d-flex align-items-start gap-0 gap-md-3 flex-column flex-md-row">
                    <div className=" w-100">
                      <StyledText
                        as="label"
                        color="secondary"
                        htmlFor={`password`}
                        className="form-label"
                      >
                        Password *
                      </StyledText>
                      <FormikInputField
                        type="password"
                        name={`password`}
                        id={`password`}
                        placeholder="Type in"
                      />
                    </div>
                    <div className="  w-100">
                      <StyledText
                        as="label"
                        color="secondary"
                        htmlFor={`confirmPassword`}
                        className="form-label"
                      >
                        Confirm Password *
                      </StyledText>
                      <FormikInputField
                        type="password"
                        name={`confirmPassword`}
                        id={`confirmPassword`}
                        placeholder="Type in"
                      />
                    </div>
                  </div>

                  <div className="my-2">
                    <PasswordGenerationModal />
                  </div>

                  <div className="my-2">
                    {process.env.REACT_APP_ENV !== 'dev' && (
                      <ReCAPTCHA
                        ref={ref}
                        sitekey={process.env.REACT_APP_CAPTCHA_APIKEY}
                        onChange={handleRecaptchaValidation}
                      />
                    )}
                    {showInvalidCaptchaMessage ? (
                      <p className="text-danger">Invalid ReCAPTCHA.</p>
                    ) : null}
                  </div>

                  <StyledButton
                    type="submit"
                    className="w-100 my-2 py-2 fw-bolder"
                    loading={loading || verifyingCaptcha || loadingData}
                    disabled={
                      loading ||
                      verifyingCaptcha ||
                      loadingData ||
                      !recaptchaVerified
                    }
                  >
                    <div className="py-1">Sign Up</div>
                  </StyledButton>

                  <div className="d-flex mb-2 justify-content-center">
                    Already have an account?
                    <Link to="/login" className="ms-1 text-decoration-none">
                      LOGIN
                    </Link>
                  </div>
                </Form>
              )}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Signup;

const StyledModalBody = styled(Modal.Body)`
  background-color: ${({ theme }) => theme.primaryLight2};
  border-radius: 0 0 5px 5px;
`;

function PasswordGenerationModal({ className }) {
  const ref = useRef();
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  return (
    <>
      <StyledButton
        type="button"
        onClick={handleShow}
        className={`px-1 mb-4 ${className}`}
        textButton
        $fontWeight="400"
        $fontSize="14px"
      >
        Generate Password
      </StyledButton>

      <Modal centered show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            <StyledText
              $fontSize={'22px'}
              $fontWeight={'500'}
              color="headingColor"
              className="mb-0"
            >
              Generate Password
            </StyledText>
          </Modal.Title>
        </Modal.Header>

        <StyledModalBody>
          <Formik
            innerRef={ref}
            validationSchema={validationSchema}
            initialValues={{
              passwordLength: 12,
              passwordStrength: 2,
              newPassword: generatePassword(12, 2),
            }}
            onSubmit={() => {}}
          >
            {({ values, errors, setFieldValue, handleSubmit }) => (
              <Form>
                <Container fluid>
                  <Row>
                    <Col xs={6}>
                      <div className="w-100 mb-3">
                        <StyledText
                          as="label"
                          color="secondary"
                          htmlFor={`passwordLength`}
                          className="form-label"
                        >
                          Password length
                        </StyledText>
                        <FormikInputField
                          type="number"
                          name={`passwordLength`}
                          id={`passwordLength`}
                          placeholder=""
                          min={8}
                          max={30}
                        />
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="w-100 mb-3">
                        <StyledText
                          as="label"
                          color="secondary"
                          htmlFor={`passwordStrength`}
                          className="form-label"
                        >
                          Password Strength
                        </StyledText>
                        <div className="px-2 mt-2">
                          <input
                            type="range"
                            class="form-range"
                            name="passwordStrength"
                            max={3}
                            min={1}
                            step={1}
                            onChange={(e) =>
                              setFieldValue('passwordStrength', e.target.value)
                            }
                          />
                        </div>
                        <div className="d-flex justify-content-between">
                          <StyledText as="p" color="bodyMedium">
                            Weak
                          </StyledText>
                          <StyledText as="p" color="bodyMedium">
                            Strong
                          </StyledText>
                        </div>
                      </div>
                    </Col>
                    <Col xs={12}>
                      <Row>
                        <Col xs={12}>
                          <StyledInputWithIcon
                            value={values.newPassword}
                            disabled={true}
                            readOnly
                            padding="6.4px 14px"
                            islight="true"
                            placeholder=" "
                            {...(values.newPassword && {
                              trailing: (
                                <OverlayTrigger
                                  overlay={
                                    <StyledTooltip>Copied</StyledTooltip>
                                  }
                                  show={copied}
                                >
                                  <span
                                    className="px-1 cursor-pointer d-flex align-items-center"
                                    onClick={() => {
                                      navigator.clipboard.writeText(
                                        values.newPassword,
                                      );
                                      setCopied(true);
                                      setTimeout(() => setCopied(false), 500);
                                    }}
                                  >
                                    <ClipboardText size={22} />
                                  </span>
                                </OverlayTrigger>
                              ),
                            })}
                          />
                          <div className="mt-3 d-flex justify-content-end">
                            <StyledButton
                              type="button"
                              $fontSize="14px"
                              className="px-3 py-2"
                              onClick={() => {
                                handleSubmit();
                                if (Object.keys(errors)?.length) return;
                                const newPassword = generatePassword(
                                  values.passwordLength,
                                  values.passwordStrength,
                                );
                                setFieldValue('newPassword', newPassword);
                              }}
                            >
                              Generate
                            </StyledButton>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Container>
              </Form>
            )}
          </Formik>
        </StyledModalBody>
      </Modal>
    </>
  );
}
